import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchUser() {
      setLoading(true);
      const ref = doc(db, "users", id);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUser(data);
        setEditFields({
          firstName: data.firstname || "",
          lastName: data.lastname || "",
          ribBic: data.ribBic || "",
          iban: data.iban || "",
          isNounou: data.isNounou || false,
          emailIsVerified: data.emailIsVerified || false,
          isVerified: data.isVerified || false,
          isActive: data.isActive !== undefined ? data.isActive : true,
        });
      }
      setLoading(false);
    }
    fetchUser();
  }, [id]);

  const saveChanges = async () => {
    setSaving(true);
    const ref = doc(db, "users", id);
    try {
      await updateDoc(ref, {
        firstname: editFields.firstName,
        lastname: editFields.lastName,
        ribBic: editFields.ribBic,
        iban: editFields.iban,
        isNounou: editFields.isNounou,
        emailIsVerified: editFields.emailIsVerified,
        isVerified: editFields.isVerified,
      });
      setUser((prev) => ({
        ...prev,
        firstname: editFields.firstName,
        lastname: editFields.lastName,
        ribBic: editFields.ribBic,
        iban: editFields.iban,
        isNounou: editFields.isNounou,
        emailIsVerified: editFields.emailIsVerified,
        isVerified: editFields.isVerified,
      }));

      // Success notification
      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Modifications sauvegardées avec succès !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
    } catch (e) {
      alert("Erreur lors de la sauvegarde");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const toggleIsNounou = async () => {
    const newVal = !editFields.isNounou;
    setEditFields((f) => ({ ...f, isNounou: newVal }));
  };

  const toggleEmailIsVerified = async () => {
    const newVal = !editFields.emailIsVerified;
    setEditFields((f) => ({ ...f, emailIsVerified: newVal }));
  };

  const toggleIsVerified = async () => {
    // Controle si pièces sont présentes avant toggle
    if (!editFields.isVerified && (!(user.documentFrontUrl || user.pieceRecto) || !(user.documentBackUrl || user.pieceVerso))) {
      alert("Impossible de vérifier le compte: pièces d'identité manquantes (recto et verso requis).");
      return;
    }
    const newVal = !editFields.isVerified;
    setEditFields((f) => ({ ...f, isVerified: newVal }));
  };

  if (!user && !loading)
    return (
      <PageTemplate
        loading={false}
        content={
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="text-center py-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-16 h-16 text-gray-300 mx-auto mb-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Utilisateur introuvable
                </h3>
                <p className="text-gray-600 mb-4">
                  L'utilisateur que vous recherchez n'existe pas ou a été supprimé.
                </p>
                <button
                  onClick={() => navigate("/users")}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#ff9be4] text-white rounded-lg font-medium hover:bg-[#ff6bd4] transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                    />
                  </svg>
                  Retour à la liste
                </button>
              </CardContent>
            </Card>
          </div>
        }
      />
    );

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-6">
          {/* Header with back button */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/users")}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-[#ff9be4] transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                />
              </svg>
              Retour à la liste
            </button>
          </div>

          {/* Info Card */}
          {user && (
            <Card className="bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200">
              <CardContent className="flex items-start gap-4">
                <div className="p-3 bg-[#ff9be4] rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-white"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Profil utilisateur
                  </h3>
                  <p className="text-sm text-gray-600">{user.email}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {user.isNounou && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                      Nounou
                    </span>
                  )}
                  {user.emailIsVerified && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium border border-green-200">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Email vérifié
                    </span>
                  )}
                  {user.isVerified && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium border border-purple-200">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.403 12.652a3 3 0 0 0 0-5.304 3 3 0 0 0-3.75-3.751 3 3 0 0 0-5.305 0 3 3 0 0 0-3.751 3.75 3 3 0 0 0 0 5.305 3 3 0 0 0 3.75 3.751 3 3 0 0 0 5.305 0 3 3 0 0 0 3.751-3.75Zm-2.546-4.46a.75.75 0 0 0-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Compte vérifié
                    </span>
                  )}
                  {user.isActive === false && (
                    <span className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium border border-red-200">
                      Inactif
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {user && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Profile Card */}
            <Card>
              <CardHeader>
                <CardTitle>Informations personnelles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Avatar */}
                <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
                  {user.photo || user.profilPhotoUrl ? (
                    <img
                      src={user.photo || user.profilPhotoUrl}
                      alt="Profil"
                      className="w-20 h-20 rounded-full object-cover ring-4 ring-pink-100"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-[#ff9be4] to-[#ff6bd4] rounded-full flex items-center justify-center text-white font-bold text-2xl">
                      {user.firstname?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {user.firstname} {user.lastname}
                    </h2>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                </div>

                {/* Form Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={editFields.firstName}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, firstName: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={editFields.lastName}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, lastName: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IBAN
                  </label>
                  <input
                    type="text"
                    value={editFields.iban}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, iban: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    BIC/SWIFT
                  </label>
                  <input
                    type="text"
                    value={editFields.ribBic}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, ribBic: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent transition-all"
                  />
                </div>

                {/* Toggles */}
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut Nounou
                    </label>
                    <button
                      onClick={toggleIsNounou}
                      className={`w-full px-4 py-2.5 rounded-lg text-white font-medium transition-colors ${
                        editFields.isNounou
                          ? "bg-purple-600 hover:bg-purple-700"
                          : "bg-gray-400 hover:bg-gray-500"
                      }`}
                      type="button"
                    >
                      {editFields.isNounou ? "Oui" : "Non"}
                    </button>
                  </div>
                </div>

                {/* Save Button */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    disabled={saving}
                    onClick={saveChanges}
                    className="w-full bg-[#ff9be4] text-white py-2.5 px-4 rounded-lg font-medium hover:bg-[#ff6bd4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sauvegarde en cours...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                        Sauvegarder les modifications
                      </>
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Statuts du compte Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Statuts du compte</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Statut Email Vérifié */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Email vérifié</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {editFields.emailIsVerified
                            ? "L'email de l'utilisateur est vérifié"
                            : "L'email de l'utilisateur n'est pas vérifié"}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                          editFields.emailIsVerified
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-gray-100 text-gray-700 border border-gray-200"
                        }`}
                      >
                        {editFields.emailIsVerified ? (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="w-3 h-3"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Vérifié
                          </>
                        ) : (
                          "Non vérifié"
                        )}
                      </span>
                    </div>
                    <button
                      onClick={toggleEmailIsVerified}
                      className={`w-full px-4 py-2.5 rounded-lg text-white font-medium transition-colors ${
                        editFields.emailIsVerified
                          ? "bg-orange-500 hover:bg-orange-600"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                      type="button"
                    >
                      {editFields.emailIsVerified
                        ? "Retirer la vérification email"
                        : "Vérifier l'email"}
                    </button>
                  </div>

                  {/* Statut Compte Vérifié */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Compte vérifié</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {editFields.isVerified
                            ? "Le compte est vérifié avec les documents"
                            : "Le compte n'est pas encore vérifié"}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                          editFields.isVerified
                            ? "bg-purple-100 text-purple-700 border border-purple-200"
                            : "bg-gray-100 text-gray-700 border border-gray-200"
                        }`}
                      >
                        {editFields.isVerified ? (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="w-3 h-3"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.403 12.652a3 3 0 0 0 0-5.304 3 3 0 0 0-3.75-3.751 3 3 0 0 0-5.305 0 3 3 0 0 0-3.751 3.75 3 3 0 0 0 0 5.305 3 3 0 0 0 3.75 3.751 3 3 0 0 0 5.305 0 3 3 0 0 0 3.751-3.75Zm-2.546-4.46a.75.75 0 0 0-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Vérifié
                          </>
                        ) : (
                          "Non vérifié"
                        )}
                      </span>
                    </div>
                    <button
                      onClick={toggleIsVerified}
                      className={`w-full px-4 py-2.5 rounded-lg text-white font-medium transition-colors ${
                        editFields.isVerified
                          ? "bg-orange-500 hover:bg-orange-600"
                          : "bg-purple-600 hover:bg-purple-700"
                      }`}
                      type="button"
                    >
                      {editFields.isVerified
                        ? "Retirer la vérification compte"
                        : "Vérifier le compte"}
                    </button>
                  </div>

                  {/* Statut Compte Actif */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Compte actif</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {user.isActive
                            ? "L'utilisateur peut accéder à l'application"
                            : "L'utilisateur ne peut pas accéder à l'application"}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                          user.isActive
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-red-100 text-red-700 border border-red-200"
                        }`}
                      >
                        {user.isActive ? "Actif" : "Inactif"}
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const ref = doc(db, "users", id);
                          await updateDoc(ref, { isActive: !user.isActive });
                          setUser((prev) => ({ ...prev, isActive: !prev.isActive }));
                        } catch {
                          alert("Erreur lors de la mise à jour");
                        }
                      }}
                      className={`w-full px-4 py-2.5 rounded-lg text-white font-medium transition-colors ${
                        user.isActive
                          ? "bg-red-500 hover:bg-red-600"
                          : "bg-green-500 hover:bg-green-600"
                      }`}
                    >
                      {user.isActive ? "Désactiver le compte" : "Activer le compte"}
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Account Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Informations du compte</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 text-gray-400 mt-0.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                        />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500">Date de création</p>
                        <p className="text-sm text-gray-900">
                          {user.createdAt
                            ? moment(user.createdAt.toDate()).format("DD/MM/YYYY HH:mm")
                            : "-"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 text-gray-400 mt-0.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
                        />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500">Connu via</p>
                        <p className="text-sm text-gray-900">{user.knowing || "-"}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Documents Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Documents de vérification</CardTitle>
                </CardHeader>
                <CardContent>
                  {(user.documentFrontUrl || user.documentBackUrl || user.casierJudiciaireUrl || user.pieceRecto || user.pieceVerso) ? (
                    <div className="space-y-4">
                      {/* Pièce d'identité */}
                      {(user.documentFrontUrl || user.pieceRecto || user.documentBackUrl || user.pieceVerso) && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">
                            Pièce d'identité
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            {(user.documentFrontUrl || user.pieceRecto) && (
                              <a
                                href={user.documentFrontUrl || user.pieceRecto}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group block border-2 border-gray-200 rounded-lg overflow-hidden hover:border-[#ff9be4] transition-all"
                              >
                                <img
                                  src={user.documentFrontUrl || user.pieceRecto}
                                  alt="Pièce d'identité Recto"
                                  loading="lazy"
                                  className="w-full h-48 object-contain bg-gray-50"
                                />
                                <div className="p-2 bg-gray-50 group-hover:bg-pink-50 transition-colors">
                                  <p className="text-xs text-center font-medium text-gray-700">
                                    Recto
                                  </p>
                                </div>
                              </a>
                            )}
                            {(user.documentBackUrl || user.pieceVerso) && (
                              <a
                                href={user.documentBackUrl || user.pieceVerso}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group block border-2 border-gray-200 rounded-lg overflow-hidden hover:border-[#ff9be4] transition-all"
                              >
                                <img
                                  src={user.documentBackUrl || user.pieceVerso}
                                  alt="Pièce d'identité Verso"
                                  loading="lazy"
                                  className="w-full h-48 object-contain bg-gray-50"
                                />
                                <div className="p-2 bg-gray-50 group-hover:bg-pink-50 transition-colors">
                                  <p className="text-xs text-center font-medium text-gray-700">
                                    Verso
                                  </p>
                                </div>
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Casier judiciaire */}
                      {user.casierJudiciaireUrl && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">
                            Casier judiciaire
                          </h4>
                          <a
                            href={user.casierJudiciaireUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block border-2 border-gray-200 rounded-lg overflow-hidden hover:border-[#ff9be4] transition-all max-w-xs"
                          >
                            <img
                              src={user.casierJudiciaireUrl}
                              alt="Casier judiciaire"
                              loading="lazy"
                              className="w-full h-48 object-contain bg-gray-50"
                            />
                            <div className="p-2 bg-gray-50 group-hover:bg-pink-50 transition-colors">
                              <p className="text-xs text-center font-medium text-gray-700">
                                Casier judiciaire
                              </p>
                            </div>
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                        />
                      </svg>
                      <p className="text-gray-500 text-sm">Aucun document reçu</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          )}
        </div>
      }
    />
  );
}